// Typed HTTP client for the CHIMERA API.
//
// - Adds Authorization: Bearer <access_token> when available
// - On 401, attempts one refresh-token exchange and retries the original request
// - On refresh failure, clears tokens and throws; the auth store reacts by redirecting to /login
// - Never retries the /auth/refresh endpoint itself (prevents loops)

import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  setTokens,
} from "./tokens";

const API_BASE = "/api/v1";

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: unknown,
  ) {
    super(typeof detail === "string" ? detail : `HTTP ${status}`);
    this.name = "ApiError";
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

async function doFetch(
  path: string,
  options: RequestOptions,
  accessToken: string | null,
): Promise<Response> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers ?? {}),
  };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  return fetch(API_BASE + path, {
    method: options.method ?? "GET",
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  });
}

async function parseErrorDetail(response: Response): Promise<unknown> {
  try {
    const data = await response.json();
    return data.detail ?? response.statusText;
  } catch {
    return response.statusText;
  }
}

async function tryRefresh(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  const response = await fetch(`${API_BASE}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });

  if (!response.ok) return null;
  const data = (await response.json()) as {
    access_token: string;
    refresh_token: string;
  };
  setTokens(data.access_token, data.refresh_token);
  return data.access_token;
}

export async function api<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  let response = await doFetch(path, options, getAccessToken());

  // 401 → try one refresh, then retry the original request once.
  // Skip the refresh dance for endpoints that are themselves the credential
  // exchange — a 401 there means bad credentials, not a stale session, and
  // we want the server's actual error message ("Invalid credentials") to
  // surface instead of being masked as "Session expired".
  const AUTH_ENDPOINTS = new Set([
    "/auth/refresh",
    "/auth/login",
    "/auth/register",
    "/auth/forgot-password",
    "/auth/reset-password",
    "/auth/2fa/login",
    "/auth/2fa/stepup",
  ]);
  if (response.status === 401 && !AUTH_ENDPOINTS.has(path)) {
    const newToken = await tryRefresh();
    if (newToken) {
      response = await doFetch(path, options, newToken);
    } else {
      clearTokens();
      throw new ApiError(401, "Session expired");
    }
  }

  if (response.status === 204) return undefined as T;

  // Superuser 2FA enrollment gate — backend returns 403 with force_2fa_enrollment
  // when a superuser hasn't enabled 2FA yet. Redirect to profile/2FA setup.
  if (response.status === 403) {
    try {
      const data = await response.clone().json();
      if (data?.detail?.force_2fa_enrollment) {
        if (window.location.pathname !== "/app/profile") {
          window.location.href = "/app/profile?setup_2fa=1";
        }
        throw new ApiError(403, data.detail);
      }
    } catch (e) {
      if (e instanceof ApiError) throw e;
      // JSON parse failed — fall through to generic error
    }
  }

  if (!response.ok) {
    const detail = await parseErrorDetail(response);
    throw new ApiError(response.status, detail);
  }

  return (await response.json()) as T;
}

/**
 * Same auth + refresh behaviour as `api()`, but returns the raw body for
 * endpoints that serve files (CSV exports) instead of JSON.
 *
 * `filename` comes from Content-Disposition; callers fall back to their own
 * name when the header is missing.
 */
export async function apiBlob(
  path: string,
  options: RequestOptions = {},
): Promise<{ blob: Blob; filename: string | null }> {
  let response = await doFetch(path, options, getAccessToken());

  if (response.status === 401) {
    const newToken = await tryRefresh();
    if (newToken) {
      response = await doFetch(path, options, newToken);
    } else {
      clearTokens();
      throw new ApiError(401, "Session expired");
    }
  }

  if (!response.ok) {
    const detail = await parseErrorDetail(response);
    throw new ApiError(response.status, detail);
  }

  const disposition = response.headers.get("Content-Disposition") ?? "";
  const match = disposition.match(/filename="?([^";]+)"?/);

  return { blob: await response.blob(), filename: match?.[1] ?? null };
}
